import React from "react";

/**
 * Icons drawn from the camera's own world rather than a generic UI set.
 *
 * The aperture mark is aperture blades. The shutter mark is a shutter
 * curtain. ND is a stack of filter squares. Every glyph refers to the
 * physical control it labels, which means the icon is doing a small amount
 * of teaching rather than just filling space — someone who doesn't know
 * what "ND" means gets a hint from the shape.
 *
 * All are 16×16 on a 24-unit grid, stroke 1.6, currentColor, and marked
 * aria-hidden: they sit beside a text label that already names the row, so
 * announcing them would just make a screen reader say everything twice.
 */

const base = {
  width: 16,
  height: 16,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.6,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": "true",
  focusable: "false",
};

const paths = {
  // Aperture blades
  aperture: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 3.5 L16 10.5" />
      <path d="M20.1 8.2 L12.5 8.2" />
      <path d="M18.9 17.3 L15 10.6" />
      <path d="M12 20.5 L8 13.5" />
      <path d="M3.9 15.8 L11.5 15.8" />
      <path d="M5.1 6.7 L9 13.4" />
    </>
  ),
  // Shutter curtain, half open
  shutter: (
    <>
      <rect x="3" y="4.5" width="18" height="15" rx="2" />
      <path d="M3 12h18" />
      <path d="M7 8.5h4" />
    </>
  ),
  // Gain / signal steps
  iso: (
    <>
      <path d="M4 19v-4" />
      <path d="M9 19v-8" />
      <path d="M14 19v-11" />
      <path d="M19 19v-14" />
    </>
  ),
  // Colour temperature
  kelvin: (
    <>
      <path d="M12 14.8V5.2a2.2 2.2 0 0 1 4.4 0v9.6a4 4 0 1 1-4.4 0Z" />
      <path d="M6.5 8H9" />
      <path d="M6.5 12H9" />
    </>
  ),
  // Filter squares, stacked
  nd: (
    <>
      <rect x="4" y="4" width="12" height="12" rx="1.5" />
      <path d="M8 20h10a2 2 0 0 0 2-2V8" />
    </>
  ),
  // Frame / resolution
  frame: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 9.5h3M18 9.5h3M3 14.5h3M18 14.5h3" />
    </>
  ),
  // Film strip / frame rate
  fps: (
    <>
      <rect x="2.5" y="6" width="19" height="12" rx="1.5" />
      <path d="M7 6v12M17 6v12" />
      <path d="M4.7 9h.01M4.7 15h.01M19.3 9h.01M19.3 15h.01" />
    </>
  ),
  // Layered profile / LUT
  profile: (
    <>
      <path d="M12 3.5 3.5 8 12 12.5 20.5 8 12 3.5Z" />
      <path d="M3.5 12.5 12 17l8.5-4.5" />
      <path d="M3.5 17 12 21.5 20.5 17" />
    </>
  ),
  // Focus target
  focus: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M4 8V5.5A1.5 1.5 0 0 1 5.5 4H8" />
      <path d="M16 4h2.5A1.5 1.5 0 0 1 20 5.5V8" />
      <path d="M20 16v2.5a1.5 1.5 0 0 1-1.5 1.5H16" />
      <path d="M8 20H5.5A1.5 1.5 0 0 1 4 18.5V16" />
    </>
  ),
  // Gimbal / stabilization
  stabilize: (
    <>
      <circle cx="12" cy="12" r="3.2" />
      <path d="M12 2.5v3M12 18.5v3M2.5 12h3M18.5 12h3" />
      <path d="M6.2 6.2 8.3 8.3M15.7 15.7l2.1 2.1M17.8 6.2l-2.1 2.1M8.3 15.7l-2.1 2.1" />
    </>
  ),
  // Contrast half-disc
  contrast: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 3.5v17a8.5 8.5 0 0 0 0-17Z" fill="currentColor" stroke="none" />
    </>
  ),
  // Saturation droplet
  saturation: <path d="M12 3.5s6 6.1 6 10a6 6 0 0 1-12 0c0-3.9 6-10 6-10Z" />,
  // Sharpness / edge
  sharpness: (
    <>
      <path d="M4 18 12 5l8 13" />
      <path d="M8.5 15h7" />
    </>
  ),
  // Crop / aspect ratio
  aspect: (
    <>
      <path d="M7 3v14h14" />
      <path d="M3 7h14v14" />
    </>
  ),
  // Memory card
  card: (
    <>
      <path d="M6 3h9l4 4v14H6Z" />
      <path d="M10 3v4M13 3v4M16 4.2V7" />
    </>
  ),
  // Pan arc
  pan: (
    <>
      <path d="M3.5 16a8.5 8.5 0 0 1 17 0" />
      <path d="M12 16v4" />
      <path d="M9 20h6" />
    </>
  ),
  // Sun / lighting condition
  light: (
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2.5M12 19.5V22M2 12h2.5M19.5 12H22M4.9 4.9l1.8 1.8M17.3 17.3l1.8 1.8M19.1 4.9l-1.8 1.8M6.7 17.3l-1.8 1.8" />
    </>
  ),
  // Chevron, for disclosures
  chevron: <path d="M8.5 5.5 15 12l-6.5 6.5" />,
};

export default function Icon({ name, size = 16, className = "" }) {
  const path = paths[name];
  if (!path) return null;
  return (
    <svg {...base} width={size} height={size} className={`icon icon-${name} ${className}`.trim()}>
      {path}
    </svg>
  );
}

// Which glyph labels which setting row. Kept here rather than in the
// template so one mapping serves every surface that lists settings.
export const SETTING_ICONS = {
  resolution: "frame",
  aspectRatio: "aspect",
  fps: "fps",
  shutter: "shutter",
  iso: "iso",
  whiteBalance: "kelvin",
  colorTemperature: "kelvin",
  profile: "profile",
  sharpness: "sharpness",
  contrast: "contrast",
  saturation: "saturation",
  ndFilter: "nd",
  ndStops: "aperture",
  stabilization: "stabilize",
  focus: "focus",
  sdCard: "card",
  panning: "pan",
};
